import { JSX, useMemo } from 'react';
import {
  CheckCircle as CompletedIcon,
  Cancel as FailedIcon,
  WorkHistory as InProgressIcon,
  NotStarted as NotStartedIcon,
  Pending as PendingIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { StyledInputAdornment } from 'layouts/Main/components/BulkActionsLedger/components/BulkActionsToolbar/BulkActionsToolbar';
import { ReportStatusRow } from 'layouts/Main/components/ExcelExportDialogV2/FirmExcelExportDialog/components';
import { normalizeFilterInput } from 'utilities';
import { ReportStatusesDisplayProps } from './ReportStatusesDisplay.props';
import {
  COMPLETED_VALUE,
  FAILED_VALUE,
  IN_PROGRESS_VALUE,
  NOT_STARTED_VALUE,
  WAITING_PICK_UP_VALUE,
} from '../../../constants';

const statusIconMap: Record<number, JSX.Element> = {
  [IN_PROGRESS_VALUE]: <InProgressIcon />,
  [COMPLETED_VALUE]: <CompletedIcon />,
  [FAILED_VALUE]: <FailedIcon />,
  [WAITING_PICK_UP_VALUE]: <PendingIcon />,
  [NOT_STARTED_VALUE]: <NotStartedIcon />,
};

const ReportStatusesDisplay = ({
  reportStatuses,
  companySearchString,
  setCompanySearchString,
  handleGlobalToggle,
  handleIndividualChange,
  reportsToRequest,
}: ReportStatusesDisplayProps) => {
  const visibleReportStatuses = useMemo(() => {
    if (!reportStatuses) {
      return [];
    }
    if (!companySearchString) {
      return reportStatuses;
    }
    return reportStatuses.filter(rs => (rs.company_name?.toLowerCase() ?? '').includes(companySearchString));
  }, [reportStatuses, companySearchString]);

  return (
    <>
      <Paper elevation={1} sx={{ width: 'calc(100% - 25px)', marginLeft: '25px' }}>
        <div style={{ margin: '1em auto', paddingTop: '1em' }}>
          <TextField
            variant="standard"
            placeholder="Find a company"
            fullWidth
            value={companySearchString}
            onChange={e => setCompanySearchString(normalizeFilterInput(e.target.value))}
            slotProps={{
              input: {
                startAdornment: (
                  <StyledInputAdornment position="start">
                    <SearchIcon />
                  </StyledInputAdornment>
                ),
              },
            }}
            style={{ marginRight: 16 }}
          />
        </div>
      </Paper>
      <Paper elevation={2} sx={{ width: 'calc(100% - 25px)', marginLeft: '25px' }}>
        <TableContainer>
          <FormGroup>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '12%' }}>
                    <FormControlLabel
                      control={<Checkbox onChange={handleGlobalToggle} name="all" />}
                      label="Select All"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>COMPANY</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', align: 'right', width: '50%' }}>REPORT STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReportStatuses.map(reportStatus => (
                  <ReportStatusRow
                    key={reportStatus.company_measurement_date}
                    reportStatus={reportStatus}
                    icon={statusIconMap[reportStatus.status!]}
                    handleIndividualChange={handleIndividualChange}
                    checked={reportsToRequest[reportStatus.company_measurement_date]}
                  />
                ))}
              </TableBody>
            </Table>
          </FormGroup>
        </TableContainer>
      </Paper>
    </>
  );
};

export default ReportStatusesDisplay;
